import { Building2, Loader2 } from "lucide-react";
import { EditIconButton } from "@/components/ui/EditIconButton";
import { EditDatePicker } from "@/components/ui/EditDatePicker";
import { CurrencyInput } from "@/components/ui/CurrencyInput";
import { OnScreenModal } from "@/components/ui/OnScreenModal";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { NoteBanner } from "@/components/ui/NoteBanner";
import { useGoogleTranslateRefresh } from "@/hooks/useGoogleTranslateRefresh";
import { RENT_AMOUNT_MAX_DIGITS_BEFORE_DECIMAL } from "@/features/invitee/utils/inviteeValidators";
import inviteeAddStyles from "@/features/invitee/styles/inviteeAdd.module.css";
import styles from "../../../styles/applicationDetail.module.css";
import editStyles from "../../../styles/editForm.module.css";
import modalStyles from "../../../styles/modals.module.css";
import {
  EDIT_LEASE_DURATION_MONTHS,
  PROPERTY_NOT_WORKING_ALERT_MESSAGE,
  RENT_LEASE_REASONS,
} from "../propertyTabHelpers";
import type { PropertyMovingToEditSectionProps } from "../../../types/tabs";

export function PropertyMovingToEditSection({
  formData,
  property,
  selectedPropertyData,
  displayInviteCode,
  lookingUpInvite,
  validatingInvite,
  propertyOptions,
  propertyOptionsLoading,
  propertyTabStates,
  leaseDurationOptions,
  hasContractData,
  showRentLeaseReason,
  showNotWorkingAlert,
  isAdvanceSearchOpen,
  advanceSearchSelectedId,
  advanceSearchState,
  advanceSearchCity,
  advanceSearchStates,
  advanceSearchCities,
  advanceSearchProperties,
  advanceSearchLoadingStates,
  advanceSearchLoadingProperties,
  propertyInlineMessage,
  onInputChange,
  onInviteLookup,
  onValidateInvite,
  onOpenAdvanceSearch,
  onCloseAdvanceSearch,
  onApplyAdvanceSearch,
  onAdvanceSearchStateChange,
  onAdvanceSearchCityChange,
  onAdvanceSearchPropertyChange,
  onPropertySelect,
  onLoadProperties,
  onRentAmountFocus,
  onRentAmountBlur,
  onLeaseStartBlur,
  onOpenRentChangePopup,
}: PropertyMovingToEditSectionProps) {
  // The has-applied Yes/No branches and rent-lease-reason reveal new labeled fields on
  // demand; re-run Google Translate so Spanish applies to them (Angular keeps them in-DOM).
  useGoogleTranslateRefresh(
    formData.has_applied,
    showRentLeaseReason,
    showNotWorkingAlert,
  );

  return (
    <div className={styles.section} data-testid="application-property-tab-section-moving-to">
      <h2 className={styles.sectionTitle}>
        <span className={styles.sectionTitleIcon} aria-hidden="true">
          <Building2 className="h-4 w-4" />
        </span>
        Where are you moving to?
      </h2>

      {showNotWorkingAlert && (
        <NoteBanner
          className="mb-4"
          data-testid="application-property-tab-not-working-alert"
        >
          {PROPERTY_NOT_WORKING_ALERT_MESSAGE}
        </NoteBanner>
      )}

      <div className={editStyles.formRow}>
        <div className="flex w-full flex-col items-start gap-12 md:flex-row">
          <div className={`${editStyles.formGroup} ${styles.propertyAppliedQuestionGroup}`}>
            <label className={`${editStyles.label} whitespace-nowrap`} data-testid="application-property-tab-have-applied-question-label">
              Have you applied to a property?<span className={editStyles.required}>*</span>
            </label>
            <div className="flex flex-wrap gap-4">
              <button
                type="button"
                className={`px-6 py-2 border rounded-md transition-colors ${
                  formData.has_applied !== false
                    ? 'bg-[#1597ff] text-white border-[#1597ff]'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
                data-testid="application-property-tab-have-applied-yes-button"
                onClick={() => onInputChange("has_applied", true)}
              >
                Yes
              </button>
              <button
                type="button"
                className={`px-6 py-2 border rounded-md transition-colors ${
                  formData.has_applied === false
                    ? 'bg-[#1597ff] text-white border-[#1597ff]'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
                data-testid="application-property-tab-have-applied-no-button"
                onClick={() => onInputChange("has_applied", false)}
              >
                No
              </button>
            </div>
            {formData.has_applied !== false && (
              <div className={`${inviteeAddStyles.formGroup} ${styles.propertyNameTopField}`}>
                <label className={inviteeAddStyles.formLabel} htmlFor="property_id" data-testid="application-property-tab-property-id-label">
                  Property Name<span className={inviteeAddStyles.requiredStar}>*</span>{" "}
                  <a
                    href="#"
                    className="text-sm text-blue-600 hover:underline ml-3"
                    data-testid="application-property-tab-advance-search-open-link"
                    onClick={(e) => {
                      e.preventDefault();
                      onOpenAdvanceSearch();
                    }}
                  >
                    Advance Search
                  </a>
                </label>
                <SearchableSelect
                  triggerDataTestId="application-property-tab-property-select-input"
                  options={propertyOptions}
                  value={formData.property_id || ""}
                  onChange={(v) => onPropertySelect(String(v))}
                  placeholder="All Properties"
                  searchPlaceholder="Search By Property Name, City, State"
                  error={propertyInlineMessage("property_name")}
                  onOpen={() => {
                    if (propertyOptions.length <= 1) void onLoadProperties("");
                  }}
                  onSearch={(q) => void onLoadProperties(q)}
                  loading={propertyOptionsLoading}
                  className={inviteeAddStyles.customFormControl}
                />
              </div>
            )}
          </div>

          <div className="flex-1 w-full min-w-0">
            <label className={editStyles.label} data-testid="application-property-tab-invite-code-label">Have an Invite Code?</label>
            <p className={styles.propertyEditNote}>
              Note: If you are unsure or if you have been told an invitation
              has been generated for you, use the Invite Code field (or
              click &apos;Look Up&apos;) to populate your information.
            </p>
            <div className="mt-2 flex items-center gap-2 max-w-md">
              <input 
                type="text" 
                className={`${editStyles.input} min-w-0 flex-1`}
                placeholder="Invite Code"
                value={formData.invite_code || ''} 
                data-testid="application-property-tab-invite-code-input"
                onChange={(e) => onInputChange("invite_code", e.target.value)}
                readOnly={Boolean(displayInviteCode)}
              />
              {!displayInviteCode ? (
                <>
                  <button
                    type="button"
                    className="shrink-0 whitespace-nowrap px-4 py-2 bg-[#1597ff] border border-[#1597ff] text-white rounded-md hover:bg-[#0f78cc] disabled:opacity-50"
                    data-testid="application-property-tab-invite-validate-button"
                    onClick={() => onValidateInvite()}
                    disabled={validatingInvite || !formData.invite_code}
                  >
                    {validatingInvite ? "Validating…" : "Validate"}
                  </button>
                  <button
                    type="button"
                    className="shrink-0 whitespace-nowrap px-4 py-2 bg-[#1597ff] border border-[#1597ff] text-white rounded-md hover:bg-[#0f78cc] disabled:opacity-50"
                    data-testid="application-property-tab-invite-lookup-button"
                    onClick={() => void onInviteLookup()}
                    disabled={lookingUpInvite}
                  >
                    {lookingUpInvite ? "Looking…" : "Look Up"}
                  </button>
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {formData.has_applied === false && (
        <div className={styles.propertyEditNote}>
          Please stop your application and apply at your property's rental office before you continue your application.
        </div>
      )}

      {formData.has_applied !== false && (
        <>
          <OnScreenModal
            isOpen={isAdvanceSearchOpen}
            title="Advance Property Search"
            maxWidth="max-w-640"
            onClose={onCloseAdvanceSearch}
            overlayDataTestId="application-property-tab-advance-search-modal"
            panelDataTestId="application-property-tab-advance-search-panel"
            titleDataTestId="application-property-tab-advance-search-title"
            closeButtonDataTestId="application-property-tab-advance-search-close-button"
            footer={
              <div className={modalStyles.advanceSearchFooterActions}>
                <button
                  type="button"
                  className={modalStyles.modalActionSecondary}
                  data-testid="application-property-tab-advance-search-cancel-button"
                  onClick={onCloseAdvanceSearch}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={modalStyles.modalActionPrimary}
                  data-testid="application-property-tab-advance-search-apply-button"
                  onClick={onApplyAdvanceSearch}
                  disabled={!advanceSearchSelectedId || advanceSearchLoadingStates}
                >
                  Apply
                </button>
              </div>
            }
          >
            <div data-testid="application-property-tab-advance-search-body">
              {advanceSearchLoadingStates ? (
                <div
                  className={modalStyles.advanceSearchLoading}
                  role="status"
                  aria-live="polite"
                  data-testid="application-property-tab-advance-search-loading"
                >
                  <Loader2
                    className={`${modalStyles.advanceSearchLoadingSpinner} animate-spin`}
                    aria-hidden
                  />
                  <span>Loading</span>
                </div>
              ) : (
                <div className={modalStyles.advanceSearchFormGrid}>
                  <div className={inviteeAddStyles.formGroup}>
                    <label
                      className={inviteeAddStyles.formLabel}
                      htmlFor="advance_search_state"
                      data-testid="application-property-tab-advance-search-state-label"
                    >
                      State
                    </label>
                    <select
                      id="advance_search_state"
                      className={inviteeAddStyles.customFormControl}
                      value={advanceSearchState}
                      data-testid="application-property-tab-advance-search-state-input"
                      onChange={(e) => onAdvanceSearchStateChange(e.target.value)}
                    >
                      <option value="">Select State</option>
                      {advanceSearchStates.map((s) => (
                        <option key={s.state_code} value={s.state_code}>
                          {s.state_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className={inviteeAddStyles.formGroup}>
                    <label
                      className={inviteeAddStyles.formLabel}
                      htmlFor="advance_search_city"
                      data-testid="application-property-tab-advance-search-city-label"
                    >
                      City
                    </label>
                    <SearchableSelect
                      triggerDataTestId="application-property-tab-advance-search-city-input"
                      options={advanceSearchCities}
                      value={advanceSearchCity || undefined}
                      onChange={(v) => onAdvanceSearchCityChange(String(v))}
                      placeholder="All City"
                      searchPlaceholder="Search By Name"
                      invokeSearchOnClose={false}
                      className={inviteeAddStyles.customFormControl}
                    />
                  </div>

                  <div className={inviteeAddStyles.formGroup}>
                    <label
                      className={inviteeAddStyles.formLabel}
                      htmlFor="advance_search_property"
                      data-testid="application-property-tab-advance-search-property-label"
                    >
                      Property Name
                    </label>
                    <SearchableSelect
                      triggerDataTestId="application-property-tab-advance-search-property-input"
                      options={advanceSearchProperties}
                      value={advanceSearchSelectedId || undefined}
                      onChange={(v) => onAdvanceSearchPropertyChange(String(v))}
                      placeholder="All Properties"
                      searchPlaceholder="Search By Property Name or City or State or Zipcode"
                      loading={advanceSearchLoadingProperties}
                      invokeSearchOnClose={false}
                      className={inviteeAddStyles.customFormControl}
                    />
                  </div>
                </div>
              )}
            </div>
          </OnScreenModal>

          <div className={editStyles.formRow}>
            <ReadOnlyInput label="Street" testField="street" value={selectedPropertyData?.property_address || property?.property_address || ''} />
            <ReadOnlyInput label="City" testField="city" value={selectedPropertyData?.property_city || property?.property_city || ''} />
            <div className={editStyles.formGroup}>
              <label className={editStyles.label} data-testid="application-property-tab-state-label">State</label>
              <select
                className={`${editStyles.select} bg-gray-50 cursor-not-allowed`}
                value={formData.property_state || ""}
                data-testid="application-property-tab-state-select"
                disabled
                aria-readonly="true"
              >
                <option value="">Select State</option>
                {formData.property_state &&
                  !propertyTabStates.some(
                    (s) => String(s.state_code) === String(formData.property_state),
                  ) && (
                    <option value={formData.property_state}>{formData.property_state}</option>
                  )}
                {propertyTabStates.map((s) => (
                  <option key={s.state_code} value={s.state_code}>
                    {s.state_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={editStyles.formRow}>
            <ReadOnlyInput label="Zipcode" testField="zipcode" value={selectedPropertyData?.property_zipcode || property?.property_zipcode || ''} />
            <div className={editStyles.formGroup}>
              <label className={editStyles.label} data-testid="application-property-tab-monthly-rent-amount-label">
                Monthly Rent Amount
                <span className={editStyles.required}>*</span>
              </label>
              <div className="relative">
                <CurrencyInput
                  data-testid="application-property-tab-monthly-rent-amount-input"
                  className={editStyles.input}
                  value={formData.rent_amount != null ? String(formData.rent_amount) : ""}
                  onChange={(value) => onInputChange("rent_amount", value)}
                  onFocus={onRentAmountFocus}
                  onBlur={onRentAmountBlur}
                  placeholder="$1,000.00"
                  maxDigitsBeforeDecimal={RENT_AMOUNT_MAX_DIGITS_BEFORE_DECIMAL}
                  error={propertyInlineMessage("rent_amount")}
                  readOnly={hasContractData}
                />
                {hasContractData && onOpenRentChangePopup && (
                  <EditIconButton
                    title="Edit"
                    aria-label="Edit Monthly Rent Amount"
                    data-testid="application-property-tab-monthly-rent-amount-edit-button"
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                    onClick={onOpenRentChangePopup}
                  />
                )}
              </div>
            </div>
            <div className={editStyles.formGroup}>
              <label className={editStyles.label} data-testid="application-property-tab-lease-start-date-label">
                Lease Start Date<span className={editStyles.required}>*</span>
              </label>
              <div data-testid="application-property-tab-lease-start-date-input">
                <EditDatePicker
                  value={formData.lease_start_date}
                  onChange={(value) => onInputChange("lease_start_date", value)}
                  onBlur={onLeaseStartBlur}
                  allowPastDates={true}
                  allowFutureDates={true}
                  displayFormat="mdy"
                  required
                  className={propertyInlineMessage("lease_start_date") ? editStyles.inputError : ""}
                />
                {propertyInlineMessage("lease_start_date") ? (
                  <div className={editStyles.errorMessage} role="alert">
                    {propertyInlineMessage("lease_start_date")}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className={editStyles.formRow}>
            <div className={editStyles.formGroup}>
              <label className={editStyles.label} data-testid="application-property-tab-lease-duration-label">
                Lease Duration
              </label>
              <SearchableSelect
                triggerDataTestId="application-property-tab-lease-duration-select"
                options={leaseDurationOptions}
                value={String(formData.lease_duration || EDIT_LEASE_DURATION_MONTHS)}
                onChange={(v) => onInputChange("lease_duration", String(v))}
                placeholder="Lease Duration"
                searchPlaceholder="Search Lease Duration"
                searchable={false}
                className={styles.propertyCustomSelect}
              />
            </div>
            <div className={editStyles.formGroup}>
              <label className={editStyles.label} data-testid="application-property-tab-unit-number-label">Unit Number</label>
              <input
                type="text"
                className={editStyles.input}
                value={formData.unit_number}
                data-testid="application-property-tab-unit-number-input"
                onChange={(e) => onInputChange("unit_number", e.target.value)}
                placeholder="Unit Number"
              />
            </div>
            {showRentLeaseReason && (
              <div className={editStyles.formGroup}>
                <label className={editStyles.label} data-testid="application-property-tab-rent-lease-reason-label">
                  Reason<span className={editStyles.required}>*</span>
                </label>
                <SearchableSelect
                  triggerDataTestId="application-property-tab-rent-lease-reason-select"
                  options={RENT_LEASE_REASONS}
                  value={formData.rent_lease_reason}
                  onChange={(value) =>
                    onInputChange("rent_lease_reason", String(value))
                  }
                  placeholder="Select Reason"
                  searchPlaceholder="Search Reason"
                  searchable={false}
                  className={`${styles.propertyCustomSelect}${propertyInlineMessage("rent_lease_reason") ? ` ${editStyles.inputError}` : ""}`}
                />
                {propertyInlineMessage("rent_lease_reason") ? (
                  <div className={editStyles.errorMessage} role="alert">
                    {propertyInlineMessage("rent_lease_reason")}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ReadOnlyInput({
  label,
  testField,
  value,
}: {
  label: string;
  testField: string;
  value: string;
}) {
  return (
    <div className={editStyles.formGroup}>
      <label className={editStyles.label} data-testid={`application-property-tab-${testField}-label`}>{label}</label>
      <input
        type="text"
        className={`${editStyles.input} bg-gray-50`}
        value={value}
        readOnly
        disabled
        data-testid={`application-property-tab-${testField}-input`}
      />
    </div>
  );
}
